import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

// A PNG, not present.svg, for the reason DailyMissionsCard gives: the SVG's fixed pattern id
// collides with a hidden copy of the pill on web and the icon paints nothing.
import PresentPng from '../../../assets/header/present-pill.png';
import { useDevice } from '../../../utils/hooks/useDevice';
import { Pressable } from '../../Components/common/Touchable';
import PillBase from './PillBase';

// Figma "Pill / Free Chips" (907:370) sets both lines in Body/Pill - Sofia Sans Regular 14/14,
// 2 apart - on the 126-wide Mobile pill. The Desktop variant is 216 wide but only grows the
// labels to 20/22, so a desktop layer that draws this pill scaled up by 216 / 126 asks for the
// labels at that ratio instead.
const MOBILE_LABEL = { fontSize: 14, lineHeight: 14, gap: 2 } as const;
const DESKTOP_LABEL_SCALE = 126 / 216;
const DESKTOP_LABEL = {
	fontSize: 20 * DESKTOP_LABEL_SCALE,
	lineHeight: 22 * DESKTOP_LABEL_SCALE,
	gap: 3.429 * DESKTOP_LABEL_SCALE,
} as const;

type RewardsCardProps = {
	/** The pill is being drawn for a desktop layer, scaled up: use the Desktop variant's labels. */
	desktop?: boolean;
};

// The same 126x48 pill on every screen; the header and the desktop layers scale it with ScaledBox.
const RewardsCard = ({ desktop = false }: RewardsCardProps = {}) => {
	const { t } = useTranslation();
	const { isWeb } = useDevice();
	const styles = createStyles(desktop ? DESKTOP_LABEL : MOBILE_LABEL);

	return (
		<Pressable style={[styles.card, isWeb && styles.cardWeb]}>
			<PillBase />
			<View style={styles.iconWrap}>
				<Image source={PresentPng} resizeMode='contain' style={styles.icon} />
			</View>

			<View style={styles.content}>
				<Text numberOfLines={1} style={styles.label}>
					{t('T_SITE_HOME_FREE_CHIPS', { defaultValue: 'Free chips' })}
				</Text>
				<Text numberOfLines={1} style={[styles.label, styles.ready]}>
					{t('T_SITE_HOME_READY', { defaultValue: 'Ready!' })}
				</Text>
			</View>
		</Pressable>
	);
};

const createStyles = (pillLabel: typeof MOBILE_LABEL | typeof DESKTOP_LABEL) =>
	StyleSheet.create({
		// The plate, border and glow are the design's own artwork, painted by PillBase. The content
		// row hangs 9 from the top, padded on the left only (12, then the 31.6 icon and an 8 gap),
		// which is what leaves "Free chips" its 74 at 14px.
		card: {
			width: 126,
			height: 48,
			paddingTop: 9,
			paddingLeft: 12,
			flexDirection: 'row',
			alignItems: 'flex-start',
		},
		cardWeb: {
			cursor: 'pointer',
		},
		iconWrap: {
			width: 31.557,
			height: 32.612,
			justifyContent: 'center',
			alignItems: 'center',
			marginRight: 8,
		},
		icon: {
			width: 31.557,
			height: 32.612,
		},
		content: {
			flexShrink: 1,
			gap: pillLabel.gap,
		},
		label: {
			color: '#FFFFFF',
			fontFamily: 'Sofia Sans',
			fontSize: pillLabel.fontSize,
			lineHeight: pillLabel.lineHeight,
			fontWeight: '400',
		},
		ready: {
			color: '#0CEB66',
		},
	});

export default RewardsCard;
